import React, { useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSmartSearchContext } from '../../context/smartSearch.js';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { BREAKPOINTS } from '../../../constants';
import { TRANSLATIONS } from '../../../translations.js';
import pages from '../../define/pages';
import endpoints from '../../define/endpoints';

import { Modal } from '../modals';
import SearchBar, {
  SearchBarVariants,
  SearchBarWidths,
} from '../../ui-components/SearchBar/SearchBar';
import Drawer from '../../ui-components/Drawer/Drawer';
import Typography, {
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import SmartSearchContent from './SmartSearchContent/SmartSearchContent';

import axios from 'axios';
import { useDebounce } from '../../hooks/useDebounce.js';

const SmartSearch = ({
  title,
  initialQuery,
  isNavSearch,
  isNavTransparent,
  isIconOnly,
}) => {
  const { isModalOpen, setIsModalOpen } = useSmartSearchContext();
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);

  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searchValue, setSearchValue] = useState(initialQuery ?? '');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // TODO: This should be a reusable hook as it can be used elsewhere
  const getResultLink = (item) => {
    // TODO: This should be a reusable const as it can be used elsewhere
    const BASE_URL = window.location.origin;
    const LOCALIZED_BASE_URL =
      window.freedomeConfig.locale !== window.freedomeConfig.baseLocale
        ? BASE_URL + '/' + window.freedomeConfig.locale
        : BASE_URL;

    if (item?.slug) return `${LOCALIZED_BASE_URL}${item.slug}`;

    const searchParams = new URLSearchParams();

    if (item?.search_term) searchParams.append('searchTerm', item?.search_term);
    if (item?.category_id) searchParams.append('category', item?.category_id);
    if (!item.search_term && !item.category_id)
      searchParams.append('searchTerm', item?.title);

    if (searchParams) return `${BASE_URL}${pages.catalogue}?${searchParams}`;
  };

  // TODO: This should be a reusable hook as it can be used elsewhere
  const getSuggestions = async () => {
    try {
      const response = await axios.get(
        `${endpoints.core}/search/all?query=${debouncedSearchValue}`,
        {
          params: { locale: window.freedomeConfig.locale },
        }
      );
      const results = response.data.map((element) => {
        element.link = getResultLink(element);
        return element;
      });
      dataLayer.push({
        query: debouncedSearchValue,
        suggestions: [],
        event: 'searchLoadedSuggestions',
      });
      setSuggestions(results);
    } catch (error) {
      setError(new Error(error));
      // console.error(error);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setHasFetched(false);
    setSuggestions([]);
    if (debouncedSearchValue === '') {
      setIsLoading(false);
      return;
    }
    getSuggestions(debouncedSearchValue);
  }, [debouncedSearchValue, setIsLoading, setHasFetched, setSuggestions]);

  useEffect(() => {
    setSearchValue(initialQuery ?? '');
  }, [initialQuery]);

  const smartSearchContent = (
    <SmartSearchContent
      title={title}
      isLoading={isLoading}
      hasFetched={hasFetched}
      suggestions={suggestions}
      error={error}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
    />
  );

  const drawer = (
    <Drawer
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      title={
        <Typography variant="p2" weight={TypographyWeights['semi-bold']}>
          {TRANSLATIONS.WHAT_DO_YOU_WANT_TO_DO}
        </Typography>
      }
      body={smartSearchContent}
    />
  );

  const modal = (
    <Modal
      onClickOverlay={() => setIsModalOpen(false)}
      shouldScroll={false}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
    >
      {smartSearchContent}
    </Modal>
  );

  const searchBar = (
    <SearchBar
      asButton
      variant={SearchBarVariants.primary}
      width={
        isNavSearch ? SearchBarWidths.full : SearchBarWidths['contained-center']
      }
      isElevated={true}
      handleClick={() => setIsModalOpen(true)}
      value={searchValue}
      isNavSearch={isNavSearch}
    />
  );

  return (
    <>
      {isIconOnly ? (
        <SearchBar
          asButton
          variant={SearchBarVariants.icon}
          isWhiteBackground={isNavTransparent}
          handleClick={() => setIsModalOpen(true)}
        />
      ) : (
        searchBar
      )}
      {isDeviceSmallerThanTablet ? drawer : modal}
    </>
  );
};

SmartSearch.propTypes = {
  title: PropTypes.string,
  initialQuery: PropTypes.string,
  isNavSearch: PropTypes.bool,
  isNavTransparent: PropTypes.bool,
  isIconOnly: PropTypes.bool,
};

SmartSearch.defaultProps = {
  title: null,
  initialQuery: null,
  isNavSearch: false,
  isNavTransparent: false,
  isIconOnly: false,
};

export default memo(SmartSearch);
